import type { TubeKey } from "@probetalab/contracts";
import { tubeName } from "../lib/format.js";
import type { Line } from "./StudyPicker.js";

/** A tube the order will need, previewed from the chosen lines before saving. */
export interface PlannedTube extends TubeKey {
  key: string;
  container_name: string | null;
  container_colour_hex: string | null;
  container_colour_name: string | null;
  sample_type_name: string | null;
  study_claves: string[];
}

/** The same key the server groups tubes by: container and sample type, null matching null. */
export const tubeKeyOf = (t: TubeKey) => `${t.container_id ?? ""}|${t.sample_type_id ?? ""}`;

/** Group the lines' specimens into tubes, in the order they were first chosen. */
export function planTubes(lines: Line[]): PlannedTube[] {
  const tubes = new Map<string, PlannedTube>();
  for (const l of lines) {
    for (const sp of l.specimens) {
      const key = tubeKeyOf(sp);
      let t = tubes.get(key);
      if (!t) {
        t = {
          key,
          container_id: sp.container_id,
          sample_type_id: sp.sample_type_id,
          container_name: sp.container_name,
          container_colour_hex: sp.container_colour_hex,
          container_colour_name: sp.container_colour_name,
          sample_type_name: sp.sample_type_name,
          study_claves: [],
        };
        tubes.set(key, t);
      }
      if (!t.study_claves.includes(sp.study_clave)) t.study_claves.push(sp.study_clave);
    }
  }
  return [...tubes.values()];
}

/** The `pending_tubes` payload: only keys of tubes still on the order (a removed study drops its mark). */
export function pendingTubesPayload(tubes: PlannedTube[], pending: ReadonlySet<string>): TubeKey[] {
  return tubes.filter((t) => pending.has(t.key)).map((t) => ({ container_id: t.container_id, sample_type_id: t.sample_type_id }));
}

/** The set with `key` added (`on`) or removed. */
export function withKey(set: ReadonlySet<string>, key: string, on: boolean): ReadonlySet<string> {
  const next = new Set(set);
  if (on) next.add(key);
  else next.delete(key);
  return next;
}

/**
 * For a laboratory that takes the samples at the counter (lab setting
 * `samples_collected_at_registration`): every tube is registered as collected; the receptionist
 * marks as «Pendiente» only the one the patient still owes.
 */
export function TubeChecklist({ tubes, pending, onToggle, note }: { tubes: PlannedTube[]; pending: ReadonlySet<string>; onToggle: (key: string, pending: boolean) => void; note?: string }) {
  if (!tubes.length) return null;
  return (
    <div className="panel">
      <div className="kicker" style={{ marginBottom: 4 }}>
        Tubos a etiquetar
      </div>
      <p className="small muted" style={{ margin: "0 0 8px" }}>
        Se registran como tomados. Marca como pendiente solo el que falte.{note ? ` ${note}` : ""}
      </p>
      <div className="stack" style={{ gap: 2 }}>
        {tubes.map((t) => {
          const name = tubeName(t);
          return (
            <div key={t.key} className="row" style={{ justifyContent: "space-between", gap: 12 }}>
              <span className="tube">
                <span className="tube-cap" style={{ background: t.container_colour_hex ?? "#ccc" }} />
                <span>
                  <b>{name}</b> <span className="muted small">· {t.study_claves.join(", ")}</span>
                </span>
              </span>
              <label className="switch">
                <input type="checkbox" checked={pending.has(t.key)} onChange={(e) => onToggle(t.key, e.target.checked)} aria-label={`Pendiente: ${name}`} />
                <span className="track" />
                <span>Pendiente</span>
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
